import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css } from '@coursera/coursera-ui';
import type { ClickStatus } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import Submit from 'bundles/authentication/components/Submit';
import {
  ARKOSE_SIGNUP_KEY,
  EMAIL_CONSTRAINT,
  FULLNAME_CONSTRAINT,
  PASSWORD_CONSTRAINT,
  RECAPTCHA_ACTIONS,
  SIGNUP_REDESIGN_RECAPTCHA_ID,
  TOU_CHECKBOX_CONSTRAINT,
  emailRequirementsError,
  existingEmailError,
  nameRequirementsError,
  passwordRequirementsError,
  responseMessageFactory,
} from 'bundles/authentication/constants';
import Alert from 'bundles/authentication/shared/Alert';
import EmailInput from 'bundles/authentication/shared/EmailInput';
import Form from 'bundles/authentication/shared/Form';
import NameInput from 'bundles/authentication/shared/NameInput';
import PasswordInput from 'bundles/authentication/shared/PasswordInput';
import TermsOfUseCheckbox from 'bundles/authentication/shared/TermsOfUseCheckbox';
import * as actions from 'bundles/authentication/shared/actions';
import { useAddProfileConsent } from 'bundles/authentication/shared/hooks/useAddProfileConsent';
import useBotManager from 'bundles/authentication/shared/hooks/useBotManager';
import type {
  ErrorResponse,
  EventData,
  SignupSharedProps,
  SuccessResponse,
} from 'bundles/authentication/shared/types/sharedTypes';
import { shouldShowCheckboxTermsOfUse } from 'bundles/authentication/utils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import AppInfoContext from 'bundles/page/contexts/AppInfoContext';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

type Context = {
  _eventData: EventData;
};

type Props = SignupSharedProps;

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = StyleSheet.create({
  form: {
    // This is an offset of the marginTop below to keep the space above consistent
    marginTop: '-12px',
  },
  inputContainer: {
    marginTop: '12px',
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
  },
  item: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
    ':nth-child(5)': {
      '-ms-grid-row': '5',
    },
  },
  reCAPTCHA: {
    display: 'flex',
    'justify-content': 'center',
    '&.checkbox': { 'margin-top': '8px' },
    ':nth-of-type(n) .grecaptcha-badge': { display: 'none !important' },
  },
  disclaimer: {
    margin: '0 auto !important',
  },
  submit: {
    marginTop: '12px',
  },
  styleConflictInputFix: {
    ':nth-of-type(n) input': {
      border: `0px !important`,
      height: 'unset',
      marginBottom: '0px !important',
      padding: '12px 16px',
      lineHeight: '22px',
      fontSize: '16px',
      outline: 'none',
      boxShadow: 'none !important',
      color: 'unset',
    },
    ':nth-of-type(n) input:focus:required:invalid:focus': { color: 'unset' },
    ':nth-of-type(n) input::placeholder': { color: '#767676', opacity: 1 },
    /* Internet Explorer 10-11 */
    ':nth-of-type(n) input:-ms-input-placeholder': { color: '#767676', opacity: 1 },
    /* Edge */
    ':nth-of-type(n) input:-webkit-input-placeholder': { color: '#767676', opacity: 1 },
  },
});

const SignupForm = (
  { navigateToLogin, onSuccess, arkoseBotManagerContext, verificationEmailRedirectUrl }: Props,
  { _eventData }: Context
) => {
  const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaSignup');

  const [message, setMessage] = useState<string | JSX.Element>('');
  const [isRunning, setIsRunning] = useState(false);

  const trackEvent = useTracker();
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const touCheckboxInputRef = useRef<HTMLInputElement | null>(null);
  const showCheckboxTermsOfUse = shouldShowCheckboxTermsOfUse();

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      context: arkoseBotManagerContext,
      publicKey: ARKOSE_SIGNUP_KEY,
    },
    reCAPTCHA: {
      container: SIGNUP_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.signup },
    },
  });

  const addProfileConsent = useAddProfileConsent();
  const appInfoContext = useContext(AppInfoContext);

  useEffect(() => {
    nameInputRef.current = document.getElementById('name') as HTMLInputElement;
    emailInputRef.current = document.getElementById('email') as HTMLInputElement;
    passwordInputRef.current = document.getElementById('password') as HTMLInputElement;
    touCheckboxInputRef.current = document.getElementById('touCheckbox') as HTMLInputElement;
  }, []);

  /* tracks click events to submit button regardless of successful or not */
  const handleTracking = (status: ClickStatus) => {
    trackEvent('click_button', {
      button: {
        name: 'signup_with_email',
        clickStatus: status,
      },
      modal: { name: 'signup' },
    });
  };

  /**
   * Handles errors that occur during the signup process by resetting the reCAPTCHA,
   * setting an appropriate error message, updating the UI, and focusing the input field
   * associated with the error.
   *
   * @param {ErrorResponse} response - The error response object containing the error code and other relevant information.
   */
  const handleError = (response: ErrorResponse) => {
    handleTracking('invalid');

    resetBotManager();

    const { errorCode = '' } = response?.responseJSON || {};

    const messages = responseMessageFactory();

    if (messages[errorCode]) {
      if (errorCode === existingEmailError) {
        const errMessageForExistingAcc = messages[errorCode](navigateToLogin);
        setMessage(errMessageForExistingAcc);
        setIsRunning(false);
      } else {
        const msg = messages[errorCode as Exclude<keyof typeof messages, 'existingCourseraAccount'>];
        setMessage(msg);
        setIsRunning(false);
      }

      if (errorCode === nameRequirementsError) {
        nameInputRef.current?.focus();
      } else if (errorCode === existingEmailError || errorCode === emailRequirementsError) {
        emailInputRef.current?.focus();
      } else if (errorCode === passwordRequirementsError) {
        passwordInputRef.current?.focus();
      }
    } else {
      setMessage(
        <FormattedMessage
          message="{error} {helpCenterLink}."
          error="An unexpected error has occurred. Please try again or contact our"
          helpCenterLink={<a href="https://courserahelp.zendesk.com">{_t('Learner Help Center')}</a>}
        />
      );
      setIsRunning(false);
    }
  };

  /**
   * Handles a successful signup by setting a success message and calling the onSuccess function.
   *
   * @param {SuccessResponse} response - The success response object containing relevant information.
   *
   * @returns {ReturnType<typeof onSuccess>} The return value of the onSuccess function.
   */
  const handleSuccess = (response: SuccessResponse): ReturnType<typeof onSuccess> => {
    handleTracking('valid');
    const successMsg = responseMessageFactory().success;

    setMessage(successMsg);

    if (showCheckboxTermsOfUse) addProfileConsent();

    return onSuccess(response);
  };

  /**
   * Handles the user signup process by validating the provided information, checking the reCAPTCHA token, and
   * calling the signup action. If the signup is successful, it handles the success response; otherwise, it handles
   * the error response.
   *
   * @param {Record<string, string>} values - An object containing the user's email, name, password.
   * @returns {Promise<void | boolean | undefined>} Returns a promise that resolves to `undefined` when the function
   * execution is complete, `false` if the email domain is invalid, or a message if the reCAPTCHA token is missing.
   */
  const handleSignup = async (values: Record<string, string>): Promise<void | boolean | undefined> => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    const { email: signupEmail, name, password } = values;

    const tokenData = await getBotManagerTokenData();

    if (!tokenData.botManagerToken) {
      const msg = isCheckbox ? responseMessageFactory().missingReCaptcha : '';
      return setMessage(msg);
    }

    setIsRunning(true);

    trackAuthAttempts(_eventData, 'signup');

    try {
      const response = await actions.signup({
        email: signupEmail,
        name,
        password,
        verificationEmailRedirectUrl,
        ...tokenData,
      });
      handleSuccess(response as SuccessResponse);
    } catch (error) {
      handleError(error as ErrorResponse);
    }

    return undefined;
  };

  /**
   * Resets the displayed message when an input field is marked as invalid.
   */
  const onInvalid = () => {
    handleTracking('invalid');

    setMessage('');
  };

  /**
   * An object containing validation constraints for the name, email, and password fields.
   * Each constraint is a function that takes a string as an argument and returns a string
   * (indicating an error message) or a boolean (indicating that the input is valid).
   *
   * @type {Record<string, (arg: string) => string | boolean>}
   */
  const constraints: Record<string, (arg: string) => string | boolean> = {
    name: FULLNAME_CONSTRAINT,
    email: EMAIL_CONSTRAINT,
    password: PASSWORD_CONSTRAINT,
    touCheckbox: showCheckboxTermsOfUse ? TOU_CHECKBOX_CONSTRAINT : () => false,
  };

  return (
    <Form
      {...css('rc-SignupForm', styles.form)}
      // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
      constraints={constraints}
      name="signup"
      onSubmit={handleSignup}
      onInvalid={onInvalid}
    >
      <div aria-live="assertive">
        {message && (
          <Alert
            role="status"
            {...css(styles.item)}
            type={message === responseMessageFactory().success ? 'success' : 'error'}
          >
            {message}
          </Alert>
        )}
      </div>
      <div {...css(styles.inputContainer)}>
        <NameInput
          {...css(styles.styleConflictInputFix, styles.item)}
          // Some android devices Android automatically open the keyboard on focus, blocking SSO elements.
          autoFocus={!appInfoContext?.userAgent?.isAndroid}
          ref={nameInputRef}
          autoComplete="name"
        />

        <EmailInput
          {...css(styles.styleConflictInputFix, styles.item)}
          // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
          emailSpellingCheck
          required
          ref={emailInputRef}
        />

        <PasswordInput
          {...css(styles.styleConflictInputFix, styles.item)}
          supportText={_t('Between 8 and 72 characters')}
          ref={passwordInputRef}
          autoComplete="new-password"
        />
        <div
          {...css(styles.reCAPTCHA)}
          data-testid={SIGNUP_REDESIGN_RECAPTCHA_ID}
          id={SIGNUP_REDESIGN_RECAPTCHA_ID}
          data-size={!isCheckbox && 'invisible'}
        />

        {showCheckboxTermsOfUse && (
          <TermsOfUseCheckbox ref={touCheckboxInputRef} data-testid="signup-form-tou-checkbox" />
        )}

        <TrackedSubmit
          {...css(styles.submit, styles.item)}
          data-catchpoint="signup-form-submit-button"
          data-e2e="signup-form-submit-button"
          disabled={isRunning}
          trackingName="signup_form_submit_button"
        >
          {_t('Join for Free')}
        </TrackedSubmit>
      </div>
    </Form>
  );
};

SignupForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'signup',
  },
}))(SignupForm);
